import React, { type ReactNode } from 'react'
import {
    ToggleButton,
    ToggleButtonGroup,
    type ToggleButtonGroupProps,
} from '@mui/material'

export type ToggleButtonOption<T extends string> = {
    value: T
    label: ReactNode
    disabled?: boolean
}

export type ToggleButtonsProps<T extends string> = Omit<
    ToggleButtonGroupProps,
    'children' | 'exclusive' | 'onChange' | 'value'
> & {
    value: T
    options: ToggleButtonOption<T>[]
    onChange: (value: T) => void
}

export function ToggleButtons<T extends string>({
    value,
    options,
    onChange,
    sx,
    ...rest
}: ToggleButtonsProps<T>) {
    return (
        <ToggleButtonGroup
            exclusive
            value={value}
            onChange={(_event, nextValue: T | null) => {
                if (nextValue !== null) {
                    onChange(nextValue)
                }
            }}
            {...rest}
            sx={[
                {
                    '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.5,
                    },
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                >
                    {option.label}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    )
}
